/**
 * React Query hooks for Prospects API
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import type {
  Prospect,
  ProspectCreate,
  ProspectUpdate,
  ProspectListResponse,
  ProspectListParams,
  ProspectImportResponse,
} from '@/types/prospect';
import {
  listProspects,
  getProspect,
  createProspect,
  updateProspect,
  deleteProspect,
  importProspectsCSV,
} from '../prospects';

// Query keys
export const prospectKeys = {
  all: ['prospects'] as const,
  lists: () => [...prospectKeys.all, 'list'] as const,
  list: (params?: ProspectListParams) => [...prospectKeys.lists(), params] as const,
  details: () => [...prospectKeys.all, 'detail'] as const,
  detail: (id: string) => [...prospectKeys.details(), id] as const,
};

/**
 * List prospects with optional filters and pagination
 */
export function useProspects(
  params?: ProspectListParams
): UseQueryResult<ProspectListResponse, Error> {
  return useQuery({
    queryKey: prospectKeys.list(params),
    queryFn: () => listProspects(params),
  });
}

/**
 * Get a single prospect by ID
 */
export function useProspect(id: string): UseQueryResult<Prospect, Error> {
  return useQuery({
    queryKey: prospectKeys.detail(id),
    queryFn: () => getProspect(id),
    enabled: !!id,
  });
}

/**
 * Create a new prospect
 */
export function useCreateProspect(): UseMutationResult<
  Prospect,
  Error,
  ProspectCreate,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProspect,
    onSuccess: () => {
      // Invalidate all list queries to refetch
      queryClient.invalidateQueries({ queryKey: prospectKeys.lists() });
    },
  });
}

/**
 * Update a prospect
 */
export function useUpdateProspect(): UseMutationResult<
  Prospect,
  Error,
  { id: string; data: ProspectUpdate },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateProspect(id, data),
    onSuccess: (data, variables) => {
      // Update the specific prospect in cache
      queryClient.setQueryData(prospectKeys.detail(variables.id), data);
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: prospectKeys.lists() });
    },
  });
}

/**
 * Delete a prospect
 */
export function useDeleteProspect(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProspect,
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: prospectKeys.detail(id) });
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: prospectKeys.lists() });
    },
  });
}

/**
 * Import prospects from CSV files
 */
export function useImportCSV(): UseMutationResult<
  ProspectImportResponse,
  Error,
  File[],
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: importProspectsCSV,
    onSuccess: () => {
      // Invalidate all list queries to refetch with new prospects
      queryClient.invalidateQueries({ queryKey: prospectKeys.lists() });
    },
  });
}
